import { t } from "@lingui/macro";
import {
  CfExportDocument,
  type CfExportQuery,
  type CfExportQueryVariables,
  ClientOiExportDocument,
  type ClientOiExportQuery,
  type ClientOiExportQueryVariables,
  GetOutgoingInvoicesDocument,
  GetOutgoingInvoicesFilterOptionsDocument,
  type GetOutgoingInvoicesFilterOptionsQuery,
  type GetOutgoingInvoicesFilterOptionsQueryVariables,
  type GetOutgoingInvoicesQuery,
  type GetOutgoingInvoicesQueryVariables,
  MarkOutgoingInvoiceAsUnpaidDocument,
  type MarkOutgoingInvoiceAsUnpaidMutation,
  type MarkOutgoingInvoiceAsUnpaidMutationVariables,
  ModelLockLockableTypeEnum,
  MrpOiExportDocument,
  type MrpOiExportQuery,
  type MrpOiExportQueryVariables,
  NotPaidOiExportDocument,
  type NotPaidOiExportQuery,
  type NotPaidOiExportQueryVariables,
  OiWhereColumn,
  OutgoingInvoiceAbraFlexiExportDocument,
  type OutgoingInvoiceAbraFlexiExportQuery,
  type OutgoingInvoiceAbraFlexiExportQueryVariables,
  OutgoingInvoiceOmegaExportDocument,
  type OutgoingInvoiceOmegaExportQuery,
  type OutgoingInvoiceOmegaExportQueryVariables,
  OutgoingInvoiceOrderByColumnEnum,
  type OutgoingInvoicesSummary,
  OutgoingInvoicesTableSummaryDocument,
  type OutgoingInvoicesTableSummaryQuery,
  type OutgoingInvoicesTableSummaryQueryVariables,
  OutgoingInvoiceTypeEnum,
  PaymentStatusEnum,
  PohodaOiExportDocument,
  type PohodaOiExportQuery,
  type PohodaOiExportQueryVariables,
  PremierOiExportDocument,
  type PremierOiExportQuery,
  type PremierOiExportQueryVariables,
  ProlucOiExportDocument,
  type ProlucOiExportQuery,
  type ProlucOiExportQueryVariables,
  SortOrder,
  SqlOperator,
  VarioOiExportDocument,
  type VarioOiExportQuery,
  type VarioOiExportQueryVariables,
} from "@src/__generated__/graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import type { XeroExportModalRef } from "@src/components/modules/common/exports/xero/XeroExportModal";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import {
  getBooleanFilterOptions,
  getPaymentStatusFilterOptions,
} from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { now, toApiDate } from "@src/utils/dates";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import {
  createActiveQueryParam,
  parseActiveQueryParam,
} from "@src/utils/routing";
import { camelCase } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { Query } from "nextjs-routes";
import { createRef } from "react";
import mapToOptions from "../../../../../utils/map-to-options";
import { ChangeDatesModalStore } from "../../shared/change-dates-modal/store";
import { OutgoingInvoiceModel } from "./models";

export enum QuickFilters {
  All = "all",
  PastDuePayments = "pastDuePayments",
  // biome-ignore lint/style/useLiteralEnumMembers: Enums are always generated so it is a literal value
  Unpaid = PaymentStatusEnum.NotPaid,
  Invoices = "invoices",
  PaymentReceipts = "paymentReceipts",
  InvoiceItems = "invoiceItems",
}

type MarkAsUnpaidModalData = {
  id: string;
  hasMatchedPayments: boolean;
};

/**
 * store holding projects page related data
 */
export class StoreOutgoingInvoices {
  changeDatesModalStore = new ChangeDatesModalStore();
  markAsUnpaidModal = new DisclosureState<MarkAsUnpaidModalData>();

  pagination = new PaginationState("outgoingInvoices", {
    onChangePagination: () => {
      if (this.isInvoiceItemsActive) return;
      this.fetchOutgoingInvoices();
    },
  });
  @observable activeQuickFilter: QuickFilters = QuickFilters.All;

  orderBy = new OrderBy<OutgoingInvoiceOrderByColumnEnum>([
    {
      column: OutgoingInvoiceOrderByColumnEnum.IssueDate,
      order: SortOrder.Desc,
    },
    {
      column: OutgoingInvoiceOrderByColumnEnum.InvoiceNo,
      order: SortOrder.Desc,
    },
  ]);

  // Quick Filters
  quickFiltersWhere = new Filters<OiWhereColumn>(
    [
      new Filter({
        column: OiWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: "",
        hidden: true,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.DueDate,
        title: "",
        hidden: true,
        operator: SqlOperator.Lt,
        options: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummary();
      },
    },
  );

  // Common Filters
  where = new Filters<OiWhereColumn>(
    [
      new Filter({
        column: OiWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: OiWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.WorkspaceBankAccountId,
        operator: SqlOperator.In,
        title: t`Agency bank account`,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.OwnerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: OiWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: OiWhereColumn.IsExported,
        operator: SqlOperator.Eq,
        title: t`Exported`,
        options: getBooleanFilterOptions(),
      }),
      new Filter({
        column: OiWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        title: t`Date of supply`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.Type,
        operator: SqlOperator.In,
        title: t`Type`,
        options: [
          {
            label: t`Invoice`,
            value: camelCase(OutgoingInvoiceTypeEnum.Invoice),
          },
          {
            label: t`Payment receipt`,
            value: camelCase(OutgoingInvoiceTypeEnum.ProformaPayment),
          },
          {
            label: t`Credit note`,
            value: camelCase(OutgoingInvoiceTypeEnum.CreditNote),
          },
        ],
      }),
      new Filter({
        column: OiWhereColumn.IssueDate,
        operator: SqlOperator.Between,
        title: t`Issue date`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.DueDate,
        operator: SqlOperator.Between,
        title: t`Due date`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        title: t`Paid at`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from (with VAT)`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: OiWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to (with VAT)`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: OiWhereColumn.ProjectId,
        operator: SqlOperator.In,
        title: t`Project`,
        options: [],
      }),
      new Filter({
        column: OiWhereColumn.ProjectCategory,
        operator: SqlOperator.In,
        title: t`Project category`,
        options: [],
      }),
      new Filter({
        hidden: !appStore.workspaceStore.hasModuleForPage("time_billing"),
        column: OiWhereColumn.HasTimeBilling,
        title: t`Has time billing`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
        value: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummary();
      },
      tableKey: "outgoing-invoices",
      orderBy: this.orderBy,
    },
  );

  xeroModalRef = createRef<XeroExportModalRef>();

  private cashFlowExportFetcher = new FetchHelper<
    CfExportQuery,
    CfExportQueryVariables
  >(CfExportDocument);

  private unpaidOutgoingInvoicesExportFetcher = new FetchHelper<
    NotPaidOiExportQuery,
    NotPaidOiExportQueryVariables
  >(NotPaidOiExportDocument);

  private outgoingInvoicesExportFetcher = new FetchHelper<
    ClientOiExportQuery,
    ClientOiExportQueryVariables
  >(ClientOiExportDocument);

  private varioExportFetcher = new FetchHelper<
    VarioOiExportQuery,
    VarioOiExportQueryVariables
  >(VarioOiExportDocument);

  private outgoingInvoicesFetcher = new FetchHelper<
    GetOutgoingInvoicesQuery,
    GetOutgoingInvoicesQueryVariables
  >(GetOutgoingInvoicesDocument);

  private summaryFetcher = new FetchHelper<
    OutgoingInvoicesTableSummaryQuery,
    OutgoingInvoicesTableSummaryQueryVariables
  >(OutgoingInvoicesTableSummaryDocument);

  private filterOptionsFetcher = new FetchHelper<
    GetOutgoingInvoicesFilterOptionsQuery,
    GetOutgoingInvoicesFilterOptionsQueryVariables
  >(GetOutgoingInvoicesFilterOptionsDocument);

  private omegaExportFetcher = new FetchHelper<
    OutgoingInvoiceOmegaExportQuery,
    OutgoingInvoiceOmegaExportQueryVariables
  >(OutgoingInvoiceOmegaExportDocument);

  private abraFlexiExportFetcher = new FetchHelper<
    OutgoingInvoiceAbraFlexiExportQuery,
    OutgoingInvoiceAbraFlexiExportQueryVariables
  >(OutgoingInvoiceAbraFlexiExportDocument);

  private premierExportFetcher = new FetchHelper<
    PremierOiExportQuery,
    PremierOiExportQueryVariables
  >(PremierOiExportDocument);

  private mrpExportFetcher = new FetchHelper<
    MrpOiExportQuery,
    MrpOiExportQueryVariables
  >(MrpOiExportDocument);

  private prolucExportFetcher = new FetchHelper<
    ProlucOiExportQuery,
    ProlucOiExportQueryVariables
  >(ProlucOiExportDocument);

  private pohodaExportFetcher = new FetchHelper<
    PohodaOiExportQuery,
    PohodaOiExportQueryVariables
  >(PohodaOiExportDocument);

  private markAsUnpaidMutator = new MutationHelper<
    MarkOutgoingInvoiceAsUnpaidMutation,
    MarkOutgoingInvoiceAsUnpaidMutationVariables
  >(MarkOutgoingInvoiceAsUnpaidDocument);

  constructor() {
    makeObservable(this);
  }

  @action
  toggleQuickFilter(quickFilter: QuickFilters | string) {
    const typeFilter = this.where.filtersByColumn.get(OiWhereColumn.Type);
    const paymentStatusFilter = this.where.filtersByColumn.get(
      OiWhereColumn.PaymentStatus,
    );

    if (this.isInvoiceItemsActive) {
      this.orderBy.setClauses([
        {
          column: OutgoingInvoiceOrderByColumnEnum.IssueDate,
          order: SortOrder.Desc,
        },
        {
          column: OutgoingInvoiceOrderByColumnEnum.InvoiceNo,
          order: SortOrder.Desc,
        },
      ]);
    }

    paymentStatusFilter?.clear(true);
    paymentStatusFilter?.show();

    typeFilter?.clear(true);
    typeFilter?.show();

    switch (quickFilter) {
      case QuickFilters.PastDuePayments:
        this.activeQuickFilter = QuickFilters.PastDuePayments;

        this.quickFilterDueDate?.setValue(toApiDate(now()), {
          skipOnChange: true,
        });

        this.quickFilterPaymentStatus?.setValue([
          PaymentStatusEnum.NotPaid,
          PaymentStatusEnum.PartiallyPaid,
        ]);

        paymentStatusFilter?.hide();
        break;
      case QuickFilters.Unpaid:
        this.activeQuickFilter = QuickFilters.Unpaid;

        this.quickFilterDueDate?.clear(true);

        this.quickFilterPaymentStatus?.setValue([
          PaymentStatusEnum.NotPaid,
          PaymentStatusEnum.PartiallyPaid,
        ]);

        paymentStatusFilter?.hide();
        break;
      case QuickFilters.InvoiceItems:
        this.orderBy.setClauses([]);

        setTimeout(() => {
          this.activeQuickFilter = QuickFilters.InvoiceItems;
        });

        break;
      case QuickFilters.Invoices:
        this.activeQuickFilter = QuickFilters.Invoices;

        this.quickFilterDueDate?.clear(true);
        this.quickFilterPaymentStatus?.clear(true);

        typeFilter?.hide();
        typeFilter?.setValue([
          camelCase(OutgoingInvoiceTypeEnum.Invoice),
          camelCase(OutgoingInvoiceTypeEnum.CreditNote),
        ]);

        break;
      case QuickFilters.PaymentReceipts:
        this.activeQuickFilter = QuickFilters.PaymentReceipts;

        this.quickFilterDueDate?.clear(true);
        this.quickFilterPaymentStatus?.clear(true);

        typeFilter?.hide();
        typeFilter?.setValue([
          camelCase(OutgoingInvoiceTypeEnum.ProformaPayment),
        ]);

        break;
      default:
        this.activeQuickFilter = QuickFilters.All;

        this.quickFilterDueDate?.clear(true);
        this.quickFilterPaymentStatus?.clear();

        break;
    }
  }

  @computed
  get isInvoiceItemsActive() {
    return this.activeQuickFilter === QuickFilters.InvoiceItems;
  }

  @action
  setQuickFilterFromURL(query: Query) {
    const active = parseActiveQueryParam(query);
    this.toggleQuickFilter(active || QuickFilters.All);
  }

  @action.bound
  setSearchTerm = (val: string) => {
    this.searchTerm = val;
    this.pagination.resetPage();
    this.fetchSummary();
  };

  @computed get quickFilterPaymentStatus() {
    return this.quickFiltersWhere.filters.find(
      (f) => f.column === OiWhereColumn.PaymentStatus,
    );
  }

  @computed get quickFilterDueDate() {
    return this.quickFiltersWhere.filters.find(
      (f) => f.column === OiWhereColumn.DueDate,
    );
  }

  @observable searchTerm = "";

  @observable.ref outgoingInvoices: OutgoingInvoiceModel[] = [];

  // @observable.ref allOutgoingInvoicesIds: OutgoingInvoiceModel['id'][] = []

  @observable.ref outgoingInvoicesSummary?: Omit<
    OutgoingInvoicesSummary,
    "outgoingInvoiceInvoiceMediaIds"
  >;

  /**
   * used in bulk actions
   */
  @observable.ref selectedOutgoingInvoicesIds: OutgoingInvoiceModel["id"][] =
    [];

  clearSelection?: () => void;

  setFilterOptions(options: GetOutgoingInvoicesFilterOptionsQuery) {
    this.where.filtersByColumn
      .get(OiWhereColumn.OwnerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(options.projectManagerSimpleMap),
      );

    this.where.filtersByColumn.get(OiWhereColumn.BrandId)?.setOptions(
      options.clientsSimpleMap.map((i) => ({
        value: i.id,
        label: i.name,
        options: i.brands.map((brand) => {
          return {
            label: brand.name,
            value: brand.id,
          };
        }),
      })),
    );

    this.where.filtersByColumn
      .get(OiWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(appStore.workspaceStore.companies),
      );

    this.where.filtersByColumn
      .get(OiWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(
        appStore.workspaceStore.companies
          .flatMap(({ bankAccounts }) => bankAccounts)
          .filter(({ deleted }) => !deleted)
          .map(({ id, iban }) => ({ value: id, label: iban })),
      );

    this.where.filtersByColumn.get(OiWhereColumn.ProjectCategory)?.setOptions(
      options.projectCategories.map((category) => ({
        value: category.id,
        label: category.name,
      })),
    );

    this.where.filtersByColumn
      .get(OiWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(options.projectsSimpleMap));
  }

  fromGetOutgoingInvoicesQuery(data: GetOutgoingInvoicesQuery) {
    this.outgoingInvoices = (data.outgoingInvoices?.data || []).map(
      (i) => new OutgoingInvoiceModel(i),
    );
    if (data.outgoingInvoices) {
      this.pagination.setFromPaginatorInfo(data.outgoingInvoices.paginatorInfo);
    }
  }

  @computed get searchParams() {
    return {
      ...commonSearchParams(this),
      ...createActiveQueryParam(this.activeQuickFilter),
    };
  }

  @computed get queryParams() {
    return commonQueryVariables(this, { where: this.quickFiltersWhere });
  }

  @computed
  get isLoading(): boolean {
    return this.outgoingInvoicesFetcher.isLoading.value;
  }

  @computed
  get isSummaryLoading(): boolean {
    return this.summaryFetcher.isLoading.value;
  }

  @action.bound
  async fetchOutgoingInvoices(): Promise<void> {
    const [data, error] = await this.outgoingInvoicesFetcher.fetch(
      this.queryParams,
    );

    if (error) return;

    this.fromGetOutgoingInvoicesQuery(data);
  }

  @action.bound
  async fetchSummary(): Promise<void> {
    const [data, error] = await this.summaryFetcher.fetch(this.queryParams);

    if (error || !data.outgoingInvoicesSummary) return;

    this.outgoingInvoicesSummary = data.outgoingInvoicesSummary;
  }

  @action.bound async fetchFilterOptions(): Promise<void> {
    const [data, error] = await this.filterOptionsFetcher.fetch();

    if (error || !data) return;

    this.setFilterOptions(data);
  }

  @action.bound
  refetch(): void {
    this.fetchOutgoingInvoices();
    this.fetchSummary();
  }

  @computed get isMarkingAsUnpaid(): boolean {
    return this.markAsUnpaidMutator.isLoading.value;
  }

  @action.bound
  async markAsUnpaid(removeMatchedPayments: boolean): Promise<void> {
    const id = this.markAsUnpaidModal.additionalData?.id;
    if (!id) return;

    const [, error] = await this.markAsUnpaidMutator.mutate({
      id,
      remove_matched_payments: removeMatchedPayments,
    });
    if (error) return;

    this.refetch();
  }

  static create() {
    return new StoreOutgoingInvoices();
  }

  @action.bound async exportToVario(outgoingInvoiceIds: string[]) {
    const [data, error] = await this.varioExportFetcher.fetch({
      ids: outgoingInvoiceIds,
    });

    if (!data?.varioOIExport || error) return;

    window.open(data.varioOIExport, "_blank");
  }

  @computed
  get isOmegaExportDisabled() {
    return this.selectedOutgoingInvoicesIds.length === 0;
  }

  @computed
  get isOmegaExporting() {
    return this.omegaExportFetcher.isLoading.value;
  }

  @computed
  get isPremierExporting() {
    return this.premierExportFetcher.isLoading.value;
  }

  @computed
  get isVarioExporting() {
    return this.varioExportFetcher.isLoading.value;
  }

  @computed
  get isPremierExportDisabled() {
    return this.selectedOutgoingInvoicesIds.length === 0;
  }

  @action.bound
  async exportOmega(ids: string[] = this.selectedOutgoingInvoicesIds) {
    const [data, error] = await this.omegaExportFetcher.fetch({
      ids,
    });

    if (error || !data.omegaExport) return;

    this.refetch();
    window.location.href = data.omegaExport;
  }

  @action.bound
  async exportAbraFlexi(ids: string[] = this.selectedOutgoingInvoicesIds) {
    const [data, error] = await this.abraFlexiExportFetcher.fetch({ ids });

    if (error || !data.abraFlexiOIExport) return;

    appStore.UIStore.toast({
      title: t`ABRA export is being prepared. Check your data in ABRA in a few minutes.`,
      status: "success",
    });
  }

  @action.bound
  async exportPremier(ids: string[] = this.selectedOutgoingInvoicesIds) {
    const [data, error] = await this.premierExportFetcher.fetch({
      outgoing_invoice_ids: ids,
    });

    if (error || !data.premierOIExport) return;

    const exportResponse = data.premierOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Premier exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.premierOIExport;
  }

  @computed
  get isBulkLocking() {
    return appStore.modelLock.isBulkLocking;
  }

  @computed
  get isBulkUnlocking() {
    return appStore.modelLock.isBulkUnlocking;
  }

  @computed
  get isBulkLockToggling() {
    return this.isBulkLocking || this.isBulkUnlocking;
  }

  @action.bound async bulkLock(ids: string[]) {
    const [data, error] = await appStore.modelLock.bulkLockModel(
      ids,
      ModelLockLockableTypeEnum.OutgoingInvoice,
    );

    if (error || !data?.bulkLockModel) return false;

    appStore.UIStore.toast({
      status: data.bulkLockModel.failed === 0 ? "success" : "warning",
      title: t`${data.bulkLockModel.total - data.bulkLockModel.failed} out of ${data.bulkLockModel.total} outgoing invoices locked successfully`,
    });

    this.refetch();
    this.selectedOutgoingInvoicesIds = [];

    return true;
  }

  @action.bound async bulkUnlock(ids: string[]) {
    const [data, error] = await appStore.modelLock.bulkUnlockModel(
      ids,
      ModelLockLockableTypeEnum.OutgoingInvoice,
    );

    if (error || !data?.bulkUnlockModel) return false;

    appStore.UIStore.toast({
      status: data.bulkUnlockModel.failed === 0 ? "success" : "warning",
      title: t`${data.bulkUnlockModel.total - data.bulkUnlockModel.failed} out of ${data.bulkUnlockModel.total} outgoing invoices unlocked successfully`,
    });

    this.refetch();
    this.selectedOutgoingInvoicesIds = [];

    return true;
  }

  @action.bound async lockOutgoingInvoice(id: string) {
    const [data, error] = await appStore.modelLock.lockModel(
      id,
      ModelLockLockableTypeEnum.OutgoingInvoice,
    );

    if (error || !data?.lockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: t`Outgoing invoice locked successfully`,
    });

    this.refetch();

    return true;
  }

  @action.bound async unlockOutgoingInvoice(id: string) {
    const [data, error] = await appStore.modelLock.unlockModel(
      id,
      ModelLockLockableTypeEnum.OutgoingInvoice,
    );

    if (error || !data?.unlockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: t`Outgoing invoice unlocked successfully`,
    });

    this.refetch();

    return true;
  }

  @computed
  get areExportsDisabled() {
    return this.selectedOutgoingInvoicesIds.length === 0;
  }

  @computed
  get showMrpExport() {
    return appStore.workspaceStore.settings?.exportSettings.mrp ?? false;
  }

  @computed
  get showProlucExport() {
    return appStore.workspaceStore.settings?.exportSettings.proluc ?? false;
  }

  @computed
  get showXeroExport() {
    return appStore.workspaceStore.settings?.exportSettings.xero ?? false;
  }

  @computed
  get showPohodaExport() {
    return appStore.workspaceStore.settings?.exportSettings.pohoda ?? false;
  }

  @computed
  get isExporting() {
    return (
      this.cashFlowExportFetcher.isLoading.value ||
      this.unpaidOutgoingInvoicesExportFetcher.isLoading.value ||
      this.outgoingInvoicesExportFetcher.isLoading.value ||
      this.mrpExportFetcher.isLoading.value ||
      this.pohodaExportFetcher.isLoading.value ||
      this.premierExportFetcher.isLoading.value ||
      this.omegaExportFetcher.isLoading.value ||
      this.abraFlexiExportFetcher.isLoading.value ||
      this.varioExportFetcher.isLoading.value
    );
  }

  @action.bound
  async exportCashFlow(from: Date, to: Date) {
    const [data, error] = await this.cashFlowExportFetcher.fetch({
      from: toApiDate(from),
      to: toApiDate(to),
    });

    if (error || !data.cfExport) return;

    const exportResponse = data.cfExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Cash Flow exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.cfExport;
  }

  @action.bound
  async exportUnpaidOutgoingInvoices() {
    const [data, error] = await this.unpaidOutgoingInvoicesExportFetcher.fetch({
      filters: this.queryParams.filters,
    });

    if (error || !data.notPaidOIExport) return;

    const exportResponse = data.notPaidOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Unpaid Outgoing Invoices exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.notPaidOIExport;
  }

  @action.bound
  async exportOutgoingInvoices() {
    const [data, error] = await this.outgoingInvoicesExportFetcher.fetch({
      filters: this.queryParams.filters,
    });

    if (error || !data.clientOIExport) return;

    const exportResponse = data.clientOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Outgoing Invoices exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.clientOIExport;
  }

  @action.bound
  async exportMrp(from: Date, to: Date) {
    const [data, error] = await this.mrpExportFetcher.fetch({
      from: toApiDate(from),
      to: toApiDate(to),
    });

    if (error || !data.mrpOIExport) return;

    const exportResponse = data.mrpOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`MRP exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.mrpOIExport;
  }

  @action.bound
  async exportProluc(from: Date, to: Date) {
    const [data, error] = await this.prolucExportFetcher.fetch({
      from: toApiDate(from),
      to: toApiDate(to),
    });

    if (error || !data.prolucOIExport) return;

    const exportResponse = data.prolucOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Proluc exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.prolucOIExport;
  }

  @action.bound
  async exportPohoda() {
    const [data, error] = await this.pohodaExportFetcher.fetch({
      ids: this.selectedOutgoingInvoicesIds,
    });

    if (error || !data.pohodaOIExport) return;

    const exportResponse = data.pohodaOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Premier exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.pohodaOIExport;
  }
}
